import Singleton from "../Singleton";

import {
  MediaGatewayClientError,
  MediaGatewayClientErrorResponse,
} from "./Error";
import { USER_BOOKMARKS_QUERY, USER_PROFILE_QUERY } from "./queries";
import {
  GetUserProfileParams,
  GetUserBookmarksParams,
  MediaGatewayClientParams,
  GetUserProfileData,
  type Bookmark,
  type QueryUserBookmarksArgs,
  type QueryUserArgs,
} from "./types";

export default class MediaGatewayClient extends Singleton {
  #host: string;
  #password: string;
  #username: string;

  constructor(params: MediaGatewayClientParams) {
    super();

    if (!params.host) {
      throw new Error(
        `No host passed to MediaGatewayClient constructor; aborting`
      );
    }

    if (!params.username) {
      throw new Error(
        `No username passed to MediaGatewayClient constructor; aborting`
      );
    }

    if (!params.password) {
      throw new Error(
        `No password passed to MediaGatewayClient constructor; aborting`
      );
    }

    this.#host = `${params.host}/graphql`;
    this.#username = params.username;
    this.#password = params.password;
  }

  private getAuthHeader() {
    return {
      Authorization: `Basic ${btoa(`${this.#username}:${this.#password}`)}`,
    };
  }

  async getUserBookmarks({
    bconnectId,
  }: GetUserBookmarksParams): Promise<Bookmark[]> {
    this.validateGetUserBookmarks({ bconnectId });

    const variables: QueryUserBookmarksArgs = {
      bconnectId,
    };

    const payload = {
      operationName: "getUserBookmarks",
      variables,
      query: USER_BOOKMARKS_QUERY,
    };

    const options = this.#getRequestOptions(payload);

    const response = await fetch(this.#host, options);
    const json = await response.json();

    if (!response.ok) {
      throw new MediaGatewayClientError({
        errorResponse: json.errors as MediaGatewayClientErrorResponse[],
        status: response.status,
        url: this.#host,
      });
    }

    return json?.data?.userBookmarks.bookmarks;
  }

  async getUserProfile({
    bconnectId,
    sessionId,
  }: GetUserProfileParams): Promise<GetUserProfileData> {
    this.validateGetUserProfile({ bconnectId, sessionId });

    const variables: QueryUserArgs = {
      userIdentity: {
        bconnectId,
        sessionId,
      },
    };

    const payload = {
      operationName: "getUserProfile",
      variables,
      query: USER_PROFILE_QUERY,
    };

    const options = this.#getRequestOptions(payload);

    const response = await fetch(this.#host, options);
    const json = await response.json();

    if (!response.ok) {
      throw new MediaGatewayClientError({
        errorResponse: json.errors as MediaGatewayClientErrorResponse[],
        status: response.status,
        url: this.#host,
      });
    }

    return json?.data?.user.profile;
  }

  #getRequestOptions = (payload: {
    operationName: string;
    variables: QueryUserArgs | QueryUserBookmarksArgs;
    query: string;
  }): RequestInit => ({
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...this.getAuthHeader(),
    },
    body: JSON.stringify(payload),
  });

  validateGetUserBookmarks = ({ bconnectId }: GetUserBookmarksParams) => {
    if (!bconnectId) {
      throw new Error(
        `[getUserBookmarks] Missing params - bconnectId=${bconnectId}`
      );
    }
  };

  validateGetUserProfile = ({
    bconnectId,
    sessionId,
  }: GetUserProfileParams) => {
    if (!bconnectId) {
      throw new Error(`[getUserProfile] Missing param - bconnectId`);
    }

    if (!sessionId) {
      throw new Error(`[getUserProfile] Missing param - sessionId`);
    }
  };
}
